import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LimaHeader from "../components/LimaHeader";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, ShieldAlert } from "lucide-react";

const OPERATION_LOCAIS = ["Sede/oficina", "Clientes", "Obras", "Industrial"];
const OPERATION_ATIVIDADES = [
  ["Máquinas", "Máquinas / manutenção"],
  ["Solda", "Soldagem / corte / esmerilhamento"],
  ["Estruturas", "Estruturas metálicas / montagem"],
  ["Eletrica", "Instalações elétricas"],
  ["Obra", "Obras / alvenaria"],
  ["Cargas", "Içamento / movimentação de cargas"],
];
const PREPONDERANTES = [
  "Instalação de máquinas/equipamentos",
  "Usinagem/tornearia/solda",
  "Manutenção industrial",
  "Montagem de estruturas metálicas",
  "Montagem industrial",
  "Instalação/manutenção elétrica",
  "Alvenaria",
];

const CRITICAL_QUESTIONS = [
  ["altura", "Trabalho em altura — NR-35", "A empresa executa trabalho em altura acima de 2m com risco de queda?"],
  ["eletrica", "Eletricidade — NR-10", "A empresa executa instalações/manutenção elétrica ou trabalha próxima a instalações energizadas?"],
  ["maquinas", "Máquinas — NR-12", "A empresa opera, instala ou mantém máquinas e equipamentos?"],
  ["solda", "Soldagem/trabalho a quente — NR-34", "Há soldagem, corte, esmerilhamento ou trabalho a quente?"],
  ["cargas", "Movimentação de cargas — NR-11", "Há içamento, ponte rolante, talha, guindaste, empilhadeira ou acessórios de içamento?"],
  ["confinado", "Espaço confinado — NR-33", "A empresa entra em tanques, silos, galerias, poços ou outros espaços confinados?"],
  ["obra", "Obras/canteiros — NR-18", "A empresa atua em canteiros ou frentes de obra abrangidas pela NR-18?"],
  ["quimicos", "Produtos químicos — NR-09/26", "Há uso/armazenamento de produtos químicos, gases, solventes, tintas, óleos?"],
  ["vasos", "Vasos de pressão/caldeiras — NR-13", "A empresa possui/opera caldeiras, vasos de pressão ou tubulações de vapor?"],
];

const HISTORY_QUESTIONS = [
  ["acidentes", "Acidentes de trabalho nos últimos 5 anos?"],
  ["quase", "Registros de incidentes/quase acidentes?"],
  ["fiscalizacao", "A empresa já recebeu fiscalização, auto de infração, embargo ou interdição do MTE?"],
  ["passivos", "Existem processos ou passivos trabalhistas relacionados a SST?"],
  ["cliente", "Algum cliente já bloqueou trabalhador/contrato por documentação de SST?"],
];

function YesNoUnknown({ value, onChange, name }) {
  return (
    <RadioGroup value={value || ""} onValueChange={onChange} className="flex gap-6 mt-2">
      {["Sim", "Não", "NS"].map(v => (
        <label key={v} className="flex items-center gap-2 cursor-pointer text-sm">
          <RadioGroupItem value={v} id={`${name}-${v}`} data-testid={`${name}-${v}`} />
          <span>{v === "NS" ? "Não sei" : v}</span>
        </label>
      ))}
    </RadioGroup>
  );
}

export default function Diagnostic() {
  const { companyId } = useParams();
  const nav = useNavigate();
  const [company, setCompany] = useState(null);
  const [modules, setModules] = useState({});
  const [activeModules, setActiveModules] = useState([]);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [answers, setAnswers] = useState({
    operation: { locais: [], atividades: [], preponderante: "", em_clientes: "" },
    critical: {},
    modules: {},   // { "NR-01": { pgr: "Sim", ... } }
    history: {},
    evidences: { docs: "", problemas: "", obs: "" },
    contact: { nome: "", email: "", whatsapp: "", telefone: "", melhor_horario: "", aceite_contato: true },
  });

  useEffect(() => {
    api.get(`/companies/${companyId}`)
      .then(r => setCompany(r.data))
      .catch(() => { toast.error("Empresa não encontrada"); nav("/dashboard"); });
    api.get("/engine/modules").then(r => setModules(r.data));
  }, [companyId, nav]);

  // Recompute active modules whenever operation/critical answers change
  useEffect(() => {
    api.post("/engine/active-modules", { answers }).then(r => setActiveModules(r.data.active));
  }, [answers.operation, answers.critical]);

  const activeNonAlways = activeModules.filter(k => modules[k] && !modules[k].always);
  const alwaysModules = activeModules.filter(k => modules[k]?.always);
  const orderedModules = [...alwaysModules, ...activeNonAlways];

  // Steps: 0=Operação, 1=Atividades críticas, 2..N=Módulos ativos, N+1=Histórico, N+2=Evidências, N+3=Enviar
  const steps = useMemo(() => {
    const base = [
      { key: "operation", name: "Operação" },
      { key: "critical", name: "Atividades críticas" },
    ];
    orderedModules.forEach(k => base.push({ key: `mod:${k}`, name: modules[k]?.name || k }));
    base.push({ key: "history", name: "Histórico" });
    base.push({ key: "evidences", name: "Evidências" });
    base.push({ key: "contact", name: "Contato para devolutiva" });
    return base;
  }, [orderedModules, modules]);

  const total = steps.length;
  const progress = ((step) / Math.max(1, total - 1)) * 100;
  const current = steps[step];

  const updateOp = (patch) => setAnswers(a => ({ ...a, operation: { ...a.operation, ...patch } }));
  const setModAnswer = (modKey, qkey, v) => setAnswers(a => ({
    ...a,
    modules: { ...a.modules, [modKey]: { ...(a.modules[modKey] || {}), [qkey]: v } }
  }));
  const setCritical = (k, v) => setAnswers(a => ({ ...a, critical: { ...a.critical, [k]: v } }));
  const setHistory = (k, v) => setAnswers(a => ({ ...a, history: { ...a.history, [k]: v } }));

  const submit = async () => {
    // Validate contact
    const ct = answers.contact || {};
    if (!ct.nome?.trim() || !ct.email?.trim() || !ct.whatsapp?.trim()) {
      toast.error("Preencha nome, email e WhatsApp para concluir");
      return;
    }
    if (!ct.aceite_contato) {
      toast.error("É necessário autorizar o contato do consultor");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/diagnostics", { company_id: companyId, answers });
      toast.success("Diagnóstico registrado!");
      nav(`/concluido/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao registrar diagnóstico");
    } finally { setSubmitting(false); }
  };

  const next = () => step < total - 1 ? setStep(step + 1) : submit();
  const prev = () => setStep(Math.max(0, step - 1));

  if (!company) return <div className="p-10 text-slate-500">Carregando...</div>;

  return (
    <div className="min-h-screen bg-[#f4f6f8]">
      <LimaHeader />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-8">
        {/* Header block */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <span className="text-[11px] tracking-[0.28em] font-bold text-[#c7a45a]">DIAGNÓSTICO ADAPTATIVO</span>
              <h1 className="font-display font-black text-2xl md:text-3xl tracking-tight text-[#10243f] truncate">
                {company.razao_social}
              </h1>
              <p className="text-sm text-slate-500 mt-1">{company.cnpj} · {company.empregados} empregados · {company.endereco}</p>
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-500">Etapa</div>
              <div className="font-display font-black text-2xl text-[#10243f]">{step + 1}/{total}</div>
            </div>
          </div>
          <div className="mt-5">
            <div className="flex justify-between text-xs text-slate-500 mb-2">
              <span className="font-semibold uppercase tracking-widest text-[#10243f]">{current.name}</span>
              <span><strong className="text-[#10243f]">{Math.round(progress)}%</strong> · {activeModules.length} módulos NR ativos</span>
            </div>
            <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#c7a45a] to-[#e4c77b] transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {steps.map((s, i) => (
                <div
                  key={i}
                  className={`h-1.5 flex-1 min-w-[8px] rounded-full transition-colors ${
                    i < step ? "bg-[#c7a45a]" : i === step ? "bg-[#10243f]" : "bg-slate-200"
                  }`}
                  title={s.name}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6 md:p-10">
          {current.key === "operation" && (
            <div className="space-y-8">
              <div>
                <h2 className="font-display font-bold text-2xl text-[#10243f] mb-1">Como a empresa realmente trabalha?</h2>
                <p className="text-sm text-slate-500">Estas respostas ativarão apenas os módulos NR pertinentes.</p>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Onde os empregados trabalham?</Label>
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  {OPERATION_LOCAIS.map(l => (
                    <label key={l} className="flex items-center gap-3 p-3 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50">
                      <Checkbox
                        data-testid={`local-${l}`}
                        checked={answers.operation.locais.includes(l)}
                        onCheckedChange={(v) => updateOp({
                          locais: v ? [...answers.operation.locais, l] : answers.operation.locais.filter(x => x !== l)
                        })}
                      />
                      <span className="text-sm">{l}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Quais atividades são efetivamente executadas hoje?</Label>
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  {OPERATION_ATIVIDADES.map(([v, label]) => (
                    <label key={v} className="flex items-center gap-3 p-3 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50">
                      <Checkbox
                        data-testid={`ativ-${v}`}
                        checked={answers.operation.atividades.includes(v)}
                        onCheckedChange={(chk) => updateOp({
                          atividades: chk ? [...answers.operation.atividades, v] : answers.operation.atividades.filter(x => x !== v)
                        })}
                      />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Atividade preponderante</Label>
                  <Select value={answers.operation.preponderante}
                          onValueChange={(v) => updateOp({ preponderante: v })}>
                    <SelectTrigger className="mt-2" data-testid="preponderante"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {PREPONDERANTES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Atua dentro de instalações de clientes?</Label>
                  <YesNoUnknown name="em_clientes" value={answers.operation.em_clientes}
                                onChange={(v) => updateOp({ em_clientes: v })} />
                </div>
              </div>

              <div className="bg-[#10243f]/5 border border-[#10243f]/10 rounded-md p-4 flex gap-3">
                <ShieldAlert className="text-[#c7a45a] shrink-0" size={20} />
                <p className="text-sm text-slate-700">
                  Com base nas atividades marcadas, o motor calculará automaticamente os
                  módulos NR aplicáveis nas próximas etapas.
                </p>
              </div>
            </div>
          )}

          {current.key === "critical" && (
            <div className="space-y-6">
              <div>
                <h2 className="font-display font-bold text-2xl text-[#10243f] mb-1">Atividades críticas</h2>
                <p className="text-sm text-slate-500">Cada "Sim" ou "Não sei" ativa o módulo NR correspondente com suas perguntas específicas.</p>
              </div>
              <div className="divide-y divide-slate-100">
                {CRITICAL_QUESTIONS.map(([k, title, q]) => (
                  <div key={k} className="py-5">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="font-semibold text-[#10243f]">{title}</div>
                        <div className="text-sm text-slate-600 mt-1">{q}</div>
                      </div>
                    </div>
                    <YesNoUnknown name={`crit-${k}`} value={answers.critical[k]} onChange={(v) => setCritical(k, v)} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {current.key?.startsWith("mod:") && (() => {
            const modKey = current.key.slice(4);
            const mod = modules[modKey];
            if (!mod) return null;
            return (
              <div className="space-y-6">
                <div>
                  <span className="text-[11px] tracking-[0.28em] font-bold text-[#c7a45a]">MÓDULO ATIVO</span>
                  <h2 className="font-display font-bold text-2xl text-[#10243f] mt-1">{mod.name}</h2>
                  {!mod.always && (
                    <p className="text-sm text-slate-500 mt-1">Módulo ativado por resposta nas atividades da empresa.</p>
                  )}
                </div>
                <div className="divide-y divide-slate-100">
                  {mod.questions.map((q) => {
                    const qkey = q[0], qtext = q[1], hint = q[2];
                    return (
                      <div key={qkey} className="py-5">
                        <div className="text-sm text-slate-800 font-medium">{qtext}</div>
                        {hint && (
                          <div className="mt-2 p-3 bg-[#c7a45a]/8 border-l-2 border-[#c7a45a] rounded-r text-xs text-slate-600 leading-relaxed">
                            <span className="font-bold text-[#10243f]">Por que isso importa: </span>{hint}
                          </div>
                        )}
                        <YesNoUnknown
                          name={`${modKey}-${qkey}`}
                          value={answers.modules[modKey]?.[qkey]}
                          onChange={(v) => setModAnswer(modKey, qkey, v)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {current.key === "history" && (
            <div className="space-y-6">
              <div>
                <h2 className="font-display font-bold text-2xl text-[#10243f] mb-1">Histórico e exposição</h2>
                <p className="text-sm text-slate-500">Ocorrências passadas indicam risco elevado de reincidência.</p>
              </div>
              <div className="divide-y divide-slate-100">
                {HISTORY_QUESTIONS.map(([k, q]) => (
                  <div key={k} className="py-4">
                    <div className="text-sm text-slate-800">{q}</div>
                    <YesNoUnknown name={`his-${k}`} value={answers.history[k]} onChange={(v) => setHistory(k, v)} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {current.key === "evidences" && (
            <div className="space-y-6">
              <div>
                <h2 className="font-display font-bold text-2xl text-[#10243f] mb-1">Evidências e observações</h2>
                <p className="text-sm text-slate-500">Estas informações qualificam a análise técnica final.</p>
              </div>
              <div>
                <Label>Documentos SST que a empresa consegue apresentar imediatamente</Label>
                <Textarea data-testid="ev-docs" rows={3}
                  value={answers.evidences.docs}
                  onChange={e => setAnswers({ ...answers, evidences: { ...answers.evidences, docs: e.target.value } })} />
              </div>
              <div>
                <Label>Problemas de SST que a direção já sabe que precisa resolver</Label>
                <Textarea data-testid="ev-problemas" rows={3}
                  value={answers.evidences.problemas}
                  onChange={e => setAnswers({ ...answers, evidences: { ...answers.evidences, problemas: e.target.value } })} />
              </div>
              <div>
                <Label>Observações adicionais</Label>
                <Textarea data-testid="ev-obs" rows={3}
                  value={answers.evidences.obs}
                  onChange={e => setAnswers({ ...answers, evidences: { ...answers.evidences, obs: e.target.value } })} />
              </div>
            </div>
          )}

          {current.key === "contact" && (
            <div className="space-y-6">
              <div>
                <span className="text-[11px] tracking-[0.28em] font-bold text-[#c7a45a]">ÚLTIMA ETAPA</span>
                <h2 className="font-display font-bold text-2xl text-[#10243f] mt-1">Como o consultor pode falar com você?</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Suas respostas serão analisadas por um consultor Lima SST. Deixe seus dados
                  para receber a devolutiva detalhada.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label>Seu nome *</Label>
                  <input type="text" required data-testid="contact-nome"
                    className="mt-2 w-full px-3 py-2.5 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#c7a45a]"
                    value={answers.contact.nome}
                    onChange={e => setAnswers({ ...answers, contact: { ...answers.contact, nome: e.target.value } })} />
                </div>
                <div>
                  <Label>Email *</Label>
                  <input type="email" required data-testid="contact-email"
                    className="mt-2 w-full px-3 py-2.5 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#c7a45a]"
                    value={answers.contact.email}
                    onChange={e => setAnswers({ ...answers, contact: { ...answers.contact, email: e.target.value } })} />
                </div>
                <div>
                  <Label>WhatsApp *</Label>
                  <input type="tel" required data-testid="contact-whatsapp"
                    placeholder="(34) 99999-8888"
                    className="mt-2 w-full px-3 py-2.5 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#c7a45a]"
                    value={answers.contact.whatsapp}
                    onChange={e => setAnswers({ ...answers, contact: { ...answers.contact, whatsapp: e.target.value } })} />
                  <p className="text-xs text-slate-500 mt-1">Formato: DDD + número. O consultor Lima entrará em contato por esse canal.</p>
                </div>
                <div>
                  <Label>Telefone (opcional)</Label>
                  <input type="tel" data-testid="contact-telefone"
                    className="mt-2 w-full px-3 py-2.5 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#c7a45a]"
                    value={answers.contact.telefone}
                    onChange={e => setAnswers({ ...answers, contact: { ...answers.contact, telefone: e.target.value } })} />
                </div>
                <div className="md:col-span-2">
                  <Label>Melhor horário para contato</Label>
                  <input type="text" data-testid="contact-horario"
                    placeholder="Ex.: manhã, das 9h às 12h"
                    className="mt-2 w-full px-3 py-2.5 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#c7a45a]"
                    value={answers.contact.melhor_horario}
                    onChange={e => setAnswers({ ...answers, contact: { ...answers.contact, melhor_horario: e.target.value } })} />
                </div>
              </div>
              <label className="flex items-start gap-3 p-4 border border-slate-200 rounded-md cursor-pointer bg-slate-50">
                <Checkbox
                  data-testid="contact-aceite"
                  checked={answers.contact.aceite_contato}
                  onCheckedChange={(v) => setAnswers({ ...answers, contact: { ...answers.contact, aceite_contato: !!v } })}
                />
                <span className="text-sm text-slate-700">
                  Autorizo a Lima SST a entrar em contato pelos dados acima para apresentar a análise
                  detalhada do meu diagnóstico. Os dados serão tratados conforme a LGPD.
                </span>
              </label>
              <div className="bg-[#10243f]/5 border border-[#10243f]/10 rounded-md p-4 text-sm text-slate-700">
                Ao concluir, você verá uma tela de confirmação. Os detalhes de exposição e o plano
                de mitigação serão apresentados pessoalmente pelo consultor Lima SST.
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-6">
          <Button variant="ghost" onClick={prev} disabled={step === 0} data-testid="prev-btn">
            <ArrowLeft size={16} className="mr-2" /> Voltar
          </Button>
          <div className="text-xs text-slate-500 hidden md:block">
            {activeModules.length} módulos NR ativos ·{" "}
            {activeModules.slice(0, 8).join(", ")}{activeModules.length > 8 && "..."}
          </div>
          <Button onClick={next} disabled={submitting}
                  className="bg-[#c7a45a] hover:bg-[#b58f43] text-[#10243f] font-bold h-11 px-6"
                  data-testid="next-btn">
            {step === total - 1 ? (submitting ? "Enviando..." : "Enviar para o consultor Lima") : "Continuar"}
            <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      </main>
    </div>
  );
}
